import { useMutation, useQueryClient } from '@tanstack/react-query';
import { candidatoService } from '../../services/candidatoService';
import type { CandidatoUpdate } from '../../services/candidatoService';
import type { CandidatoCreate } from '../../models/candidato.types';
import { RECRUITMENT_CANDIDATOS_QUERY_KEYS } from './useCandidatosQuery';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/contexts/AuthContext';

export function useCandidatoMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  // Só pra registrar quem clicou (linkEnviadoPor/linkCopiadoPor) — mostrado no tooltip do
  // ícone depois de usado (ver CandidatosListPage). `undefined` se não achar nem
  // collaboratorId nem uid (não deveria acontecer autenticado, mas a function trata como
  // "sem ator" nesse caso).
  const ator =
    user?.collaboratorId || user?.uid
      ? { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '' }
      : undefined;

  const invalidarTudo = () =>
    queryClient.invalidateQueries({ queryKey: RECRUITMENT_CANDIDATOS_QUERY_KEYS.all });

  const criarCandidato = useMutation({
    mutationFn: (data: CandidatoCreate) => candidatoService.create(data),
    onSuccess: invalidarTudo,
    onError: (error: any) => toast.error('Erro ao cadastrar candidato.', error?.message || 'Erro desconhecido.'),
  });

  const atualizarCandidato = useMutation({
    mutationFn: (data: CandidatoUpdate) => candidatoService.atualizar(data),
    onSuccess: () => {
      invalidarTudo();
      toast.success('Candidato atualizado.');
    },
    onError: (error: any) => toast.error('Erro ao atualizar candidato.', error?.message || 'Erro desconhecido.'),
  });

  const excluirCandidato = useMutation({
    mutationFn: (candidatoId: string) => candidatoService.excluir(candidatoId),
    onSuccess: () => {
      invalidarTudo();
      toast.success('Candidato excluído.');
    },
    onError: (error: any) => toast.error('Erro ao excluir candidato.', error?.message || 'Erro desconhecido.'),
  });

  const enviarLink = useMutation({
    mutationFn: (candidatoId: string) => candidatoService.enviarLink(candidatoId, 'resend_email', ator),
    onSuccess: () => {
      invalidarTudo();
      toast.success('Link enviado para o candidato.');
    },
    onError: (error: any) =>
      toast.error('Erro ao enviar o link para o candidato.', error?.message || 'Erro desconhecido.'),
  });

  const copiarLink = useMutation({
    mutationFn: (candidatoId: string) => candidatoService.enviarLink(candidatoId, 'copy_link', ator),
    onSuccess: async ({ link }) => {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiado para a área de transferência.');
      invalidarTudo();
    },
    onError: (error: any) =>
      toast.error('Erro ao copiar o link do candidato.', error?.message || 'Erro desconhecido.'),
  });

  const MENSAGEM_POR_DECISAO: Record<'preenchido' | 'proposta_aceita' | 'proposta_recusada', string> = {
    preenchido: 'Decisão da proposta desfeita — voltou para "Preenchido".',
    proposta_aceita: 'Proposta marcada como aceita.',
    proposta_recusada: 'Proposta marcada como recusada.',
  };

  const marcarDecisaoProposta = useMutation({
    mutationFn: ({ candidatoId, status }: { candidatoId: string; status: 'preenchido' | 'proposta_aceita' | 'proposta_recusada' }) =>
      candidatoService.marcarDecisaoProposta(candidatoId, status),
    onSuccess: (_data, variables) => {
      invalidarTudo();
      toast.success(MENSAGEM_POR_DECISAO[variables.status]);
    },
    onError: (error: any) =>
      toast.error('Erro ao atualizar a decisão da proposta.', error?.message || 'Erro desconhecido.'),
  });

  const enviarLinkEtapa2 = useMutation({
    mutationFn: (candidatoId: string) => candidatoService.enviarLinkEtapa2(candidatoId, 'resend_email', ator),
    onSuccess: () => {
      invalidarTudo();
      toast.success('Link da Etapa 2 enviado para o candidato.');
    },
    onError: (error: any) =>
      toast.error('Erro ao enviar o link da Etapa 2.', error?.message || 'Erro desconhecido.'),
  });

  const copiarLinkEtapa2 = useMutation({
    mutationFn: (candidatoId: string) => candidatoService.enviarLinkEtapa2(candidatoId, 'copy_link', ator),
    onSuccess: async ({ link }) => {
      await navigator.clipboard.writeText(link);
      toast.success('Link da Etapa 2 copiado para a área de transferência.');
      invalidarTudo();
    },
    onError: (error: any) =>
      toast.error('Erro ao copiar o link da Etapa 2.', error?.message || 'Erro desconhecido.'),
  });

  return {
    criarCandidato,
    atualizarCandidato,
    excluirCandidato,
    enviarLink,
    copiarLink,
    marcarDecisaoProposta,
    enviarLinkEtapa2,
    copiarLinkEtapa2,
  };
}
